import { Slider } from 'antd';
import { ColorContext } from 'App';
import aware from 'assets/icons/AWAREICON.png';
import default1 from 'assets/icons/Default System Icon 1.png';
import default2 from 'assets/icons/Default System Icon 2.png';
import background from 'assets/icons/Default System Icon Background.png';
import { AlarmsContentWrapper } from 'components';
import BaseAccordion from 'components/Accordions/BaseAccordion';
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { useAlarms } from 'hooks';
import useWindow from 'hooks/useWindow';
import { useContext, useMemo } from 'react';
import 'styles/global.css';
import { alertColorScale, getSeverityLevel } from 'utils/alertUtils';

const iconMap = {
  AWARE: aware,
  'Fusion User': default1,
  'global-actions': default1,
  'Overwatch AI': default2,
  Other: background,
};

export default function AlarmHistory() {
  const { width, height } = useWindow();
  const alarms = useAlarms();
  const theme = useContext(ColorContext);

  // Helper functions

  const sourceStringCorrection = (str) => {
    return str.replace('global-actions', 'Global Action');
  };

  const timeSinceActivity = (alarmActivity) => {
    const msSinceActivity = Date.now() - new Date(alarmActivity);
    const seconds = Math.floor(msSinceActivity / 1000) % 60;
    const minutes = Math.floor(msSinceActivity / 1000 / 60) % 60;
    const hours = Math.floor(msSinceActivity / 1000 / 60 / 60) % 24;
    const days = Math.floor(msSinceActivity / 1000 / 60 / 60 / 24);
    return `
      ${days > 0 ? `${days} Days` : ''}\t
      ${hours >= 10 ? `${hours}` : `0${hours}`}:
      ${minutes >= 10 ? `${minutes}` : `0${minutes}`}:
      ${seconds >= 10 ? `${seconds}` : `0${seconds}`} hours
      `;
  };

  const shortenHeadline = (headline) => {
    return headline.split(';')[0];
  };

  const getAlarmId = (alarm) => {
    if (alarm.ga_identifier) return `GA_${alarm.ga_identifier}`;
    else if (alarm.ca_identifier) return `CA_${alarm.ca_identifier}`;
    else if (alarm.cw_identifier) return `CW_${alarm.cw_identifier}`;
    else return `ID_${alarm.id}`;
  };

  const keySort = (a, b) => {
    const keys = [
      'type',
      'startTime',
      'expireTime',
      'location',
      'lat',
      'lon',
      'nwsoffice',
      'alarm_id',
    ];

    for (const key of keys) {
      if (a.toLowerCase() === key) return -1;
      if (b.toLowerCase() === key) return 1;
    }

    return a.localeCompare(b);
  };

  const parseKey = (key) => {
    switch (key.toLowerCase()) {
      case 'type':
        return 'Alert Type';
      case 'nwsoffice':
        return 'Issuing Office';
      case 'starttime':
        return 'Start Time';
      case 'expiretime':
        return 'Expiration Time';
      case 'severity':
        return 'Severity';
      case 'timezone':
        return 'Time Zone';
      case 'latitude':
        return 'Latitude';
      case 'longitude':
        return 'Longitude';
      case 'location':
        return 'Location';
      default:
        return '';
    }
  };

  const getKeyValue = (key, alarm) => {
    if (key.toLowerCase() !== 'severity')
      return (
        <>
          {width >= 992 && (
            <p>
              <span className="fw-bold">{parseKey(key)}:</span>
              {alarm.info[key]}
            </p>
          )}
        </>
      );

    return (
      <div className="d-flex flex-lg-row flex-column align-items-lg-center h-auto p-0">
        <div
          className="d-flex justify-content-center align-items-center rounded-2 gap-2"
          style={{
            backgroundColor: alertColorScale(alarm.info[key]),
            width: 137,
            height: 36,
          }}
        >
          {parseKey(key)}: {getSeverityLevel(alarm.info[key])}
        </div>
        <div className="ms-2 rounded-2 bg-base pe-3" style={{ width: 275 }}>
          <Slider
            className="w-100 rounded-2"
            name="severity"
            min={0}
            max={100}
            step={1}
            disabled
            defaultValue={0 + alarm.info[key]}
          />
        </div>
      </div>
    );
  };

  const rows = useMemo(() => {
    return alarms.alarms?.map((alarm) => ({
      ca_identifier: alarm.ca_identifier || '',
      cw_identifier: alarm.cw_identifier || '',
      ga_identifier: alarm.ga_identifier || '',
      id: alarm.id || '',
      headline: alarm.headline,
      info: alarm.info,
      message: alarm.message,
      state: alarm.state === 1 ? 'Active' : 'Inactive',
      source: alarm.source,
      device: alarm.device,
      type: shortenHeadline(alarm.headline),
      activity: new Date(alarm.activity).toLocaleString(),
      status: timeSinceActivity(alarm.activity),
    }));
  }, [alarms.alarms]);

  const columns = [
    {
      header: 'Current State',
      accessorKey: 'state',
      Cell: ({ row, cell }) => {
        const state = cell.getValue();
        return <div className="text-white fw-bold fs-6">{state}</div>;
      },
    },
    {
      header: 'Source',
      accessorKey: 'source',
      Cell: ({ row, cell }) => {
        const source = cell.getValue();
        const imgSrc = iconMap[source];
        return (
          <div className="d-flex align-items-center gap-2 text-white fs-6">
            <div
              className="circle-fill circle-border center-content"
              style={{
                '--fill-color': theme.primaryShadow,
                '--border-color': theme.fontColor,
                '--radius': '22px',
              }}
            >
              <img src={imgSrc} alt={'source-icon'} size={25} />
            </div>
            {sourceStringCorrection(source)}
          </div>
        );
      },
    },
    {
      header: 'Device',
      accessorKey: 'device',
      Cell: ({ row, cell }) => {
        const device = cell.getValue();
        return <div className="text-white fs-6">{device}</div>;
      },
    },
    {
      header: 'Type',
      accessorKey: 'type',
      Cell: ({ row, cell }) => {
        const type = cell.getValue();
        return <div className="text-white fs-6">{type}</div>;
      },
    },
    {
      header: 'Activity',
      accessorKey: 'activity',
      Cell: ({ row, cell }) => {
        const activity = cell.getValue();
        return <div className="text-white fs-6">{activity}</div>;
      },
    },
    {
      header: 'Status',
      accessorKey: 'status',
      Cell: ({ row, cell }) => {
        const status = cell.getValue();
        return <div className="text-white fs-6">{status}</div>;
      },
    },
    {
      header: '',
      accessorKey: 'actions',
      enableSorting: false,
      Cell: () => {
        return <div></div>;
      },
    },
  ];

  return (
    <AlarmsContentWrapper
      title="Alarm History"
      pageIndex={alarms.page}
      maxPageIndex={Math.floor((alarms.totalCount - 1) / alarms.limit)} //use floor because it's an index
      onPageChanged={(_page) => {
        alarms.setPage(_page);
      }}
      onLimitChanged={(_limit) => {
        alarms.setLimit(_limit);
      }}
      onSearchChanged={(_search) => {
        alarms.setSearch(_search);
      }}
    >
      <div className="d-none d-lg-block h-100">
        {alarms.isLoading ? (
          <div className="d-flex justify-content-center align-items-center h-100 text-white fs-4">
            <WidgetLoadingAnimation />
          </div>
        ) : (
          <BaseTable
            columns={columns}
            rows={rows}
            expandable={true}
            altRowSettings={{
              alternate: true,
              color1: '#222a2d',
              color2: '#2C373B',
            }}
            customStyles={{
              cell: { borderBottom: '1px solid #56577A', cursor: 'pointer' },
            }}
            muiTablePaperProps={{
              sx: { height: '100%', display: 'flex', flexDirection: 'column' },
            }}
            muiTableContainerProps={{ sx: { flex: 1, overflowY: 'auto' } }}
            dropdownMenu={(alarm) => (
              <div className="w-100 d-flex flex-column user-select-text">
                <div className="fw-bold fs-5">{alarm.headline}</div>
                <div className="p-3">
                  {alarm.message
                    .trim()
                    .split('*')
                    .map((line, index) => {
                      if (line) return <li key={index}>{line}</li>;
                      return <div key={index}></div>;
                    })}
                </div>
                <div
                  className="p-3 d-grid gap-3 align-items-center"
                  style={{
                    '--grid-size': `${Array(Math.floor(Math.sqrt(Object.keys(alarm.info).length)))
                      .fill('1fr')
                      .join(' ')}`,
                    gridTemplateRows: 'var(--grid-size)',
                    gridTemplateColumns: 'var(--grid-size)',
                  }}
                >
                  {Object.keys(alarm.info)
                    .filter(
                      (key) => !['timezone', 'popaffected'].includes(key.toLowerCase()), //from what I can tell, we ignore the timezone and popaffected keys
                    )
                    .sort(keySort)
                    .map((key) => (
                      <div key={key}>{getKeyValue(key, alarm)}</div>
                    ))}
                  <div>
                    <span className="fw-bold">Alarm ID: </span>
                    {getAlarmId(alarm)}
                  </div>
                </div>
              </div>
            )}
          />
        )}
      </div>

      <div className="d-block d-lg-none h-100 overflow-auto">
        {alarms.isLoading ? (
          <div className="d-flex justify-content-center align-items-center h-100 text-white fs-4">
            <WidgetLoadingAnimation />
          </div>
        ) : (
          alarms.alarms?.map((alarm, index) => (
            <div className={index !== alarms.alarms.length - 1 ? 'mb-3' : ''} key={alarm.id}>
              <BaseAccordion
                key={alarm.id}
                bodyBackground="#232B33"
                header={
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="circle-fill circle-border center-content"
                      style={{
                        '--fill-color': '#1e374f',
                        '--border-color': 'white',
                        '--radius': '22px',
                      }}
                    >
                      <img src={iconMap[alarm.source]} alt={'source-icon'} size={25} />
                    </div>
                    <div className="d-flex flex-column gap-2">
                      <p>{sourceStringCorrection(alarm.source)}</p>
                      <p>{alarm.state === 1 ? 'Active' : 'Inactive'}</p>
                    </div>
                  </div>
                }
              >
                <div className="w-100 d-flex flex-column gap-2">
                  <p>
                    <span className="fw-bold">Source:</span> {alarm.source}
                  </p>
                  {alarm.device && (
                    <p>
                      <span className="fw-bold">Device ID:</span> {alarm.device}
                    </p>
                  )}
                  <p>
                    <span className="fw-bold">Activity:</span>{' '}
                    {new Date(alarm.activity).toLocaleString()}
                  </p>
                  <p>
                    <span className="fw-bold">Status:</span> {timeSinceActivity(alarm.activity)}
                  </p>
                  <p className="fw-bold">{shortenHeadline(alarm.headline)}</p>
                  <ul className="mb-0">
                    <li>{alarm.message}</li>
                  </ul>
                  <p>
                    <span className="fw-bold">Alert Type:</span> {alarm.info.type}
                  </p>
                  <p>
                    <span className="fw-bold">Start Time:</span>{' '}
                    {new Date(alarm.info.startTime).toLocaleString()}
                  </p>
                  <p>
                    <span className="fw-bold">Expire Time:</span>{' '}
                    {new Date(alarm.info.expireTime).toLocaleString()}
                  </p>
                  {alarm.info.location && (
                    <p>
                      <span className="fw-bold">Location:</span> {alarm.info.location}
                    </p>
                  )}
                  <p>
                    <span className="fw-bold">Issuing Office:</span> {alarm.info.NWSOffice}
                  </p>
                  <p>
                    <span className="fw-bold">Alarm ID:</span> {getAlarmId(alarm)}
                  </p>
                  {alarm.info.Latitude && (
                    <p>
                      <span className="fw-bold">Latitude:</span> {alarm.info.Latitude}
                    </p>
                  )}
                  {alarm.info.Longitude && (
                    <p>
                      <span className="fw-bold">Longitude:</span> {alarm.info.Longitude}
                    </p>
                  )}
                  {Object.keys(alarm.info)
                    .filter((key) => !['timezone', 'popaffected'].includes(key.toLowerCase()))
                    .sort(keySort)
                    .map((key) => (
                      <div key={key}>{getKeyValue(key, alarm)}</div>
                    ))}
                </div>
              </BaseAccordion>
            </div>
          ))
        )}
      </div>
    </AlarmsContentWrapper>
  );
}
